import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDownloadInvoiceMutation } from 'src/redux/billing/billing.endpoints';
import { showNotification } from 'src/redux/notification/notification.slice';

const GENERIC_DOWNLOAD_ERROR = 'Could not download the invoice. Please try again.';

export function useInvoiceDownload(): {
  downloadInvoicePdf: (invoiceId: string) => Promise<void>;
  downloadingInvoiceId: string | null;
  isInvoiceDownloading: (invoiceId: string) => boolean;
} {
  const dispatch = useDispatch();
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | null>(null);
  const [downloadInvoice] = useDownloadInvoiceMutation();

  const downloadInvoicePdf = useCallback(
    async (invoiceId: string) => {
      setDownloadingInvoiceId(invoiceId);
      try {
        const result = await downloadInvoice(invoiceId).unwrap();
        if (result.ok && result.url) {
          const link = document.createElement('a');
          link.href = result.url;
          link.download = '';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          dispatch(
            showNotification({
              summary: 'Invoice downloaded successfully',
              theme: 'success',
            }),
          );
          return;
        }

        dispatch(
          showNotification({
            summary: result.message || GENERIC_DOWNLOAD_ERROR,
            theme: 'warning',
          }),
        );
      } catch {
        dispatch(
          showNotification({
            summary: GENERIC_DOWNLOAD_ERROR,
            theme: 'warning',
          }),
        );
      } finally {
        setDownloadingInvoiceId(null);
      }
    },
    [dispatch, downloadInvoice],
  );

  const isInvoiceDownloading = useCallback(
    (invoiceId: string) => downloadingInvoiceId === invoiceId,
    [downloadingInvoiceId],
  );

  return {
    downloadInvoicePdf,
    downloadingInvoiceId,
    isInvoiceDownloading,
  };
}
